import { observable } from '@legendapp/state';
import * as Sentry from '@sentry/react';
import { supabase } from '../lib/supabase';
import i18n from '../i18n';
import { identifyUser, clearUserIdentity, setPersonProperties } from '../lib/analytics';
import { gatherEnrichedUserData } from '../lib/analyticsHelpers';
import { logger } from '../lib/logger';
import { notifyActivation } from '@papper/auth-notify';
import { initRevenueCat, prefetchOfferings } from '../lib/revenuecat';

// Auth state using Supabase Auth
export const auth$ = observable({
  user: null,
  session: null,
  isAuthenticated: false,
  isLoading: true,
});

// Initialize: check existing session
supabase.auth.getSession()
  .then(({ data: { session } }) => {
    if (session) {
      const userData = {
        id: session.user.id,
        username: session.user.user_metadata?.username || session.user.email?.split('@')[0],
        email: session.user.email,
      };
      auth$.user.set(userData);
      auth$.session.set(session);
      auth$.isAuthenticated.set(true);

      // Identify user in analytics (Amplitude, PostHog, Clarity, Hotjar) on initial session load
      identifyUser(userData.id, userData);

      // Set Sentry user context
      Sentry.setUser({
        id: userData.id,
        email: userData.email,
        username: userData.username,
      });
    }
    auth$.isLoading.set(false);
  })
  .catch((error) => {
    // If getSession fails, still set loading to false so app doesn't hang
    logger.error('[Mellow Auth] getSession failed', { error: error.message });
    auth$.isLoading.set(false);
  });

// Listen for auth state changes
supabase.auth.onAuthStateChange((event, session) => {
  console.log('[Mellow Auth]', event, session ? 'has session' : 'no session');

  if (event === 'SIGNED_OUT') {
    // Only clear state on explicit sign out
    auth$.user.set(null);
    auth$.session.set(null);
    auth$.isAuthenticated.set(false);

    // Clear user identity in analytics
    clearUserIdentity();

    // Clear Sentry user context
    Sentry.setUser(null);
  } else if (session) {
    // SIGNED_IN, TOKEN_REFRESHED, INITIAL_SESSION with valid session
    const userData = {
      id: session.user.id,
      username: session.user.user_metadata?.username || session.user.email?.split('@')[0],
      email: session.user.email,
      created_at: session.user.created_at,
    };
    auth$.user.set(userData);
    auth$.session.set(session);
    auth$.isAuthenticated.set(true);

    // Identify user in analytics with enriched data (async, non-blocking)
    // Only gather enriched data on SIGNED_IN to avoid delays on token refresh
    // Initialize RevenueCat on sign-in and initial session (not on token refresh)
    if (event === 'SIGNED_IN' || event === 'INITIAL_SESSION') {
      initRevenueCat(session.user.id).then(() => prefetchOfferings());
    }

    if (event === 'SIGNED_IN') {
      notifyActivation(supabase, 'mellow');
      gatherEnrichedUserData(session.user).then(enrichedData => {
        identifyUser(userData.id, userData, enrichedData);
      }).catch(err => {
        console.error('[Auth] Failed to gather enriched data:', err);
        // Fallback to basic identification
        identifyUser(userData.id, userData);
      });

      // Set permanent person properties
      setPersonProperties({
        created_at: session.user.created_at,
      });

      // Set Sentry user context
      Sentry.setUser({
        id: userData.id,
        email: userData.email,
        username: userData.username,
      });

      // Sync UI language from database (fire and forget - don't block)
      syncUILanguageFromDB(session.user.id);

      // Finalize acquisition source (fire and forget - don't block)
      finalizeAcquisitionSource(session.user.id);
    } else {
      // For token refresh, just identify with basic data (fast)
      identifyUser(userData.id, userData);

      // Update Sentry user context (lightweight)
      Sentry.setUser({
        id: userData.id,
        email: userData.email,
        username: userData.username,
      });
    }
  }
  // Ignore TOKEN_REFRESHED with null session - Supabase will retry automatically
  // This prevents random logouts during token refresh race conditions

  // Always set loading to false after handling auth state
  auth$.isLoading.set(false);
});

// Helper function to sync UI language (runs in background, doesn't block auth)
async function syncUILanguageFromDB(userId) {
  try {
    const { data } = await supabase
      .from('mellow_user_settings')
      .select('setting_value')
      .eq('user_id', userId)
      .eq('setting_key', 'ui_language')
      .eq('deleted', false)
      .maybeSingle();

    if (data?.setting_value && ['en', 'zh', 'ru'].includes(data.setting_value)) {
      // DB has preference - apply it
      i18n.changeLanguage(data.setting_value);
    } else {
      // No DB preference - save current language to DB
      const currentLang = i18n.language;
      await supabase
        .from('mellow_user_settings')
        .upsert({
          user_id: userId,
          setting_key: 'ui_language',
          setting_value: currentLang,
          updated_at: new Date().toISOString(),
        }, {
          onConflict: 'user_id,setting_key'
        });
    }
  } catch (err) {
    console.error('Failed to sync UI language:', err);
  }
}

// Login with email
export const login = async (email, password) => {
  const { data, error } = await supabase.auth.signInWithPassword({ email, password });
  if (error) throw error;
  return data;
};

// Register with email and username
export const register = async (email, username, password) => {
  const { data, error } = await supabase.auth.signUp({
    email,
    password,
    options: { data: { app_id: 'mellow', username } },
  });
  if (error) throw error;

  // Save current UI language to database for new user
  if (data.user) {
    try {
      const currentLang = i18n.language;
      await supabase
        .from('mellow_user_settings')
        .upsert({
          user_id: data.user.id,
          setting_key: 'ui_language',
          setting_value: currentLang,
          updated_at: new Date().toISOString(),
        }, {
          onConflict: 'user_id,setting_key'
        });
    } catch (err) {
      console.error('Failed to save initial UI language:', err);
      // Non-critical error, don't throw
    }
  }

  return data;
};

// Logout
export const logout = async () => {
  try {
    const { error } = await supabase.auth.signOut();
    if (error) {
      // If session is already missing, just clear local state
      if (error.name === 'AuthSessionMissingError') {
        console.log('[Mellow Auth] Session already missing, clearing local state');
        auth$.user.set(null);
        auth$.session.set(null);
        auth$.isAuthenticated.set(false);
        clearUserIdentity();
        return;
      }
      throw error;
    }
  } catch (err) {
    // Handle any signOut errors gracefully by clearing local state
    logger.error('Logout failed', { error: err.message });
    auth$.user.set(null);
    auth$.session.set(null);
    auth$.isAuthenticated.set(false);
    clearUserIdentity();
  }
};

/**
 * Finalize acquisition source - write pending access to database
 * Only writes ONCE (first-touch only)
 * @param {string} userId - User ID
 */
async function finalizeAcquisitionSource(userId) {
  try {
    // Check for pending access in sessionStorage
    const pendingAccess = sessionStorage.getItem('mellow_pending_access');

    if (!pendingAccess) {
      console.log('[Access] No pending access to finalize');
      return;
    }

    // Check if user already has acquisition_source
    const { data: existing } = await supabase
      .from('mellow_user_settings')
      .select('setting_value')
      .eq('user_id', userId)
      .eq('setting_key', 'acquisition_source')
      .eq('deleted', false)
      .maybeSingle();

    if (existing?.setting_value) {
      console.log('[Access] User already has acquisition_source:', existing.setting_value);
      // Clear pending access (don't overwrite)
      sessionStorage.removeItem('mellow_pending_access');
      return;
    }

    // Write acquisition_source to database
    const { error } = await supabase
      .from('mellow_user_settings')
      .upsert({
        user_id: userId,
        setting_key: 'acquisition_source',
        setting_value: pendingAccess,
        updated_at: new Date().toISOString(),
      }, {
        onConflict: 'user_id,setting_key'
      });

    if (error) {
      console.error('[Access] Failed to save acquisition_source:', error);
      // Don't clear pending access - will retry on next login
      return;
    }

    console.log('[Access] Saved acquisition_source:', pendingAccess);

    // Update analytics with acquisition source
    setPersonProperties({
      acquisition_source: pendingAccess,
    });

    // Clear pending access
    sessionStorage.removeItem('mellow_pending_access');

  } catch (error) {
    console.error('[Access] Failed to finalize acquisition source:', error);
    // Non-critical - don't block auth flow
  }
}

